import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { APISpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dynStyle, dynVariadic } from "trader.ts/ui";
import {
  Field,
  Form,
  Parsing,
  constantField,
  numberBox,
  selectBox,
  timeBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulachargemode,
  instrumentid,
  internalteacherid,
  lesson_location,
  lessongroupid,
  price_adapted_to_lg,
  price_formula,
  roomid,
  teacherid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import * as FA from "../../../styles/fontawesome.css.classes";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { formulaInvoicelinesForm } from "../../client/financialfields";
import { country } from "../../domain/config";
import { adaptInvoiceLineToLg } from "../../domain/finance";
import { invoicelineLG } from "../../domain/finance/types";
import { holiday, period } from "../../domain/periods";
import { chunk } from "../../lib/chunk";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import { planBiweekly, planWeekly } from "../../lib/planweekly";
import {
  dateInDaterange,
  getFirstWeekdayFrom,
  getMonday,
  makeDateRange,
} from "../../lib/time";
import {
  formulaType,
  invoicelinetype,
  parseFormulaType,
  parseWeekday,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import {
  ddMM,
  ddMMyyyy,
  renderFormulaType,
  renderPlanningMode,
} from "../../render";
import * as routes from "../../routes";
import { translateMonth } from "../../translations_specific";
import { schoolurl } from "../../types";
import {
  planningClientOptions,
  planning_slot,
  planning_slot_contents_lesson,
} from "./client";
import { locationWithSuggestion } from "./server";
import currency from "currency.js";
import { match } from "ts-pattern";

type teacherWithInstrumentsAndAbsences = {
  id: teacherid;
  internalteacherid: internalteacherid;
  instruments: { id: instrumentid }[];
  absences: {
    date: joda.LocalDate;
    time: joda.LocalTime;
    duration: number;
  }[];
};

export type lessongroupFormArgs = {
  today: joda.LocalDate;
  country: country;
  schoolurl: schoolurl;
  firstLessonDate: joda.LocalDate;
  period: period & { holidays: holiday[] };
  nextPeriod: null | (period & { holidays: holiday[] });
  weekday: joda.DayOfWeek;
  teacher: teacherWithInstrumentsAndAbsences;
  limitBy:
    | { tag: "slots"; slots: planning_slot[] }
    | {
        tag: "complement";
        duration: number;
        lessonsOfComplement: joda.LocalDate[];
      };
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> };
  instruments: planningClientOptions["allInstruments"];
  formulas: planningClientOptions["allFormulas"];
  each_period_fee_due_date: singleDueDateCalculation;
  each_period_fee_pay_during_online: boolean;
  onSuccessfulSave: (sl: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  }) => void;
};

export const lessonDurationOptions = [
  15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 90, 120, 150, 180, 240, 420, 480,
];

export function mkLessongroupForm(opts: lessongroupFormArgs) {
  const form = new Form({})
    .addField("teacher", [], () =>
      constantField({
        internalteacherid: opts.teacher.internalteacherid,
        id: opts.teacher.id,
      }),
    )
    .addField("periodid", [], () => constantField(opts.period.id))
    .addField("time", [], function () {
      return timeBox({
        initialVal: opts.sources.timeS.get(),
        label: "Start",
        previewS: opts.sources.timeS,
      });
    })
    .addField("instrument", [], function () {
      return instrumentField(opts, null);
    })
    .addField(
      "formula",
      ["time", "instrument"],
      function ({ time, instrument }) {
        return formulaField(opts, time, instrument, null);
      },
    )
    .addField("formulaValues", ["formula"], async function ({ formula }) {
      const deviateFromFormulaS = new Source(false);
      return makeFormulaValuesField<price_formula>(
        opts,
        deviateFromFormulaS,
        {
          type_: formula.type_,
          numberoflessons: formula.numberoflessons,
          lessonduration: formula.lessonduration,
          invoicelines: formula.invoicelines,
        },
        formula,
        opts.country,
      );
    })
    .addField(
      "lessondates",
      ["time", "formula", "formulaValues"],
      async function ({ time, formula, formulaValues }): Promise<
        Field<{
          lessons: joda.LocalDate[];
          holidaysSkipped: { date: joda.LocalDate; description: string }[];
        }>
      > {
        const lessondates =
          formula.planningmode === "Manual"
            ? {
                lessons: [opts.firstLessonDate],
                lessonsSkipped: [],
                holidaysSkipped: [],
                otherDaysSkipped: [],
                teacherAbsencesSkipped: [],
              }
            : formula.planningmode === "Weekly"
            ? planWeekly(
                opts.period,
                opts.nextPeriod,
                opts.teacher,
                opts.firstLessonDate,
                formulaValues.numberoflessons,
                formula.startsfromperiod,
                { time, duration: formulaValues.lessonduration },
              )
            : formula.planningmode === "Flex"
            ? planWeekly(
                opts.period,
                opts.nextPeriod,
                opts.teacher,
                opts.firstLessonDate,
                1, // We auto-create just one!
                formula.startsfromperiod,
                { time, duration: formulaValues.lessonduration },
              )
            : formula.planningmode === "Biweekly"
            ? planBiweekly(
                opts.period,
                opts.nextPeriod,
                opts.teacher,
                opts.firstLessonDate,
                formulaValues.numberoflessons,
                formula.startsfromperiod,
                { time, duration: formulaValues.lessonduration },
                opts.limitBy.tag === "complement"
                  ? opts.limitBy.lessonsOfComplement
                  : await rpc(
                      routes.getComplementLessonsBySlotSpec,
                      undefined,
                      {
                        time: time,
                        endtime: time.plusMinutes(formulaValues.lessonduration),
                        weekday: opts.weekday,
                        internalteacherid: opts.teacher.internalteacherid,
                        firstlesson: opts.period.firstday,
                        lastlesson: opts.period.lastday,
                      },
                    ).then((ls) => ls.map((l) => l.date)),
              )
            : checkAllCasesHandled(formula.planningmode);
        if ("error" in lessondates) {
          return errorField(lessondates.error);
        } else if (
          lessondates.lessons.length === 0 &&
          formula.planningmode === "Weekly" // Biweekly / Manual: just show grid
        ) {
          return errorField("Er konden geen lessen gepland worden");
        } else {
          if (formula.planningmode === "Weekly") {
            const renderedInfo = renderWeeklyLessonsPlanningInformation(
              lessondates,
              formula,
            );
            return constantFieldWithRender(lessondates, renderedInfo);
          } else if (
            formula.planningmode === "Flex" ||
            formula.planningmode === "Biweekly" ||
            formula.planningmode === "Manual"
          ) {
            const pm = formula.planningmode;
            return Promise.resolve(
              new Form({})
                .addField("lessons", [], () =>
                  pickDatesOnYearCalendarField({
                    initial: lessondates.lessons,
                    skip:
                      opts.limitBy.tag === "complement"
                        ? opts.limitBy.lessonsOfComplement
                        : [],
                    max: formulaValues.numberoflessons,
                    period: {
                      ...opts.period,
                      holidays:
                        formula.planningmode === "Manual"
                          ? []
                          : opts.period.holidays,
                    },
                    nextPeriod: opts.nextPeriod
                      ? {
                          ...opts.nextPeriod,
                          holidays:
                            formula.planningmode === "Manual"
                              ? []
                              : opts.nextPeriod.holidays,
                        }
                      : null,
                    limitWeekday: match(pm)
                      .with("Biweekly", () => opts.weekday)
                      .with("Manual", "Flex", () => null)
                      .exhaustive(),
                    showFlexExplanation: match(pm)
                      .with("Biweekly", "Manual", () => false)
                      .with("Flex", () => true)
                      .exhaustive(),
                  }),
                )
                .addField("holidaysSkipped", [], () =>
                  constantField(lessondates.holidaysSkipped),
                )
                .build(),
            );
          } else {
            return checkAllCasesHandled(formula.planningmode);
          }
        }
      },
    )
    .addField(
      "location",
      ["time", "instrument", "formulaValues", "lessondates"],
      async function ({ time, instrument, formulaValues, lessondates }) {
        return await locationField(
          lessondates.lessons.first()!,
          time,
          formulaValues,
          opts,
          instrument,
          null,
          null,
        );
      },
    )
    .build();

  return form;
}

export function renderWeeklyLessonsPlanningInformation(
  lessondates: {
    lessons: joda.LocalDate[];
  },
  formula: { numberoflessons: number; startsfromperiod: boolean },
): HTMLElement {
  const amount_lessons_planned = lessondates.lessons.length;
  const amount_lessons_to_plan = formula.numberoflessons;
  const moreThanOnePlannedText =
    amount_lessons_planned === amount_lessons_to_plan
      ? {
          text: "%amount_planned lessen worden gepland, van %first tot %last",
          bold: false,
          red: false,
        }
      : {
          text: "%amount_planned / %amount_to_plan lessen worden gepland, van %first tot %last",
          bold: true,
          red: formula.startsfromperiod ? false : true,
        };
  if (lessondates.lessons.length === 0) {
    return <span></span>;
  }
  if (lessondates.lessons.length === 1) {
    return renderPlanningRemarks([
      "Les wordt gepland op %date".replace(
        "%date",
        lessondates.lessons.first()!.format(ddMM) || "",
      ),
    ]);
  }

  return renderPlanningRemarks(
    [
      moreThanOnePlannedText.text
        .replace("%amount_planned", amount_lessons_planned.toString())
        .replace("%amount_to_plan", amount_lessons_to_plan.toString())
        .replace("%first", lessondates.lessons.first()?.format(ddMM) || "")
        .replace("%last", lessondates.lessons.last()?.format(ddMM) || ""),
    ],
    { bold: moreThanOnePlannedText.bold, red: moreThanOnePlannedText.red },
  );
}

export async function locationField(
  firstLesson: joda.LocalDate,
  time: joda.LocalTime,
  formulaValues: {
    lessonduration: number;
  },
  opts: {
    teacher: { internalteacherid: internalteacherid };
  },
  instrument: { id: instrumentid },
  initial: {
    location: lesson_location;
    roomId: roomid | null;
  } | null,
  ignoreLessongroupid: lessongroupid | null,
  rpcImpl?: <Parameters, Body, Returns>(
    spec: APISpec<Parameters, Body, Returns>,
    params: Parameters,
    b: Body,
  ) => Promise<Returns>,
): Promise<Field<locationWithSuggestion>> {
  const rooms = await (rpcImpl || rpc)(
    routes.calculateRoomSuggestionsSpec,
    undefined,
    {
      startDate: firstLesson,
      time: time,
      duration: formulaValues.lessonduration,
      internalteacherid: opts.teacher.internalteacherid,
      instrumentid: instrument.id,
      ignoreLessongroupid,
    },
  );
  // Selecting initial location
  let initialSel = rooms[0];
  if (initial !== null) {
    const initialLocParsed = initial.location;
    const found = rooms.find((r) => {
      return r.location === initialLocParsed && r.roomid === initial.roomId;
    });
    if (found) {
      initialSel = found;
    } else {
      // Current location is not in suggestions -> fall back to default
    }
  }
  function describe(room: {
    description: string;
    suggestion: null | string;
  }): string {
    return room.description + (room.suggestion ? ` (${room.suggestion})` : "");
  }
  return selectBox({
    values: rooms.orderBy([(r) => r.order, describe], ["asc", "asc"]),
    initial: initialSel,
    show: describe,
    label: "Lokaal",
    isDisabled: (r) => r.disabled,
  });
}

export async function formulaField(
  opts: {
    teacher: { internalteacherid: internalteacherid };
    weekday: joda.DayOfWeek;
    formulas: planningClientOptions["allFormulas"];
    limitBy:
      | { tag: "slots"; slots: planning_slot[] }
      | { tag: "complement"; duration: number };
  },
  time: joda.LocalTime,
  instrument: { id: instrumentid; description: string },
  initial: planningClientOptions["allFormulas"][number] | null,
) {
  const formulasToShow = (() => {
    const limitBy = opts.limitBy;
    if (limitBy.tag === "slots") {
      const firstBlockingSlot = limitBy.slots
        .filter(
          (s) =>
            isEqual(s.weekday, opts.weekday) &&
            s.internalteacherid === opts.teacher.internalteacherid &&
            (s.contents.tag === "lesson" ||
              s.contents.tag === "enrollmentrequest" ||
              s.contents.tag === "teacher_absence") &&
            (isEqual(s.time, time) || s.time.isAfter(time)),
        )
        .orderBy((s) => s.time.toSecondOfDay(), "asc")
        .first();

      const maxDuration =
        firstBlockingSlot === null
          ? 600
          : joda.Duration.between(time, firstBlockingSlot.time).toMinutes();

      return opts.formulas
        .filter(
          (f) =>
            f.instruments.includes(instrument.id) &&
            f.lessonduration <= maxDuration,
        )
        .orderBy((f) => f.description);
    } else if (limitBy.tag === "complement") {
      return opts.formulas
        .filter(
          (f) =>
            f.planningmode === "Biweekly" &&
            f.lessonduration === limitBy.duration,
        )
        .orderBy((f) => f.description);
    } else {
      return checkAllCasesHandled(limitBy);
    }
  })();

  const sb = await selectBox({
    values: formulasToShow,
    initial:
      !isNil(initial) && formulasToShow.some((f) => f.id === initial.id)
        ? initial
        : formulasToShow.length === 1
        ? formulasToShow[0]
        : null,
    show: (f) => f.description,

    label: "Formule",
  });

  const noFormulasText = renderPlanningRemarks(
    formulasToShow.length === 0 ? ["Geen formules gevonden"] : [],
  );

  return {
    ...sb,
    render: () => (
      <div>
        {sb.render()}
        {noFormulasText}
      </div>
    ),
  };
}

function instrumentField(
  opts: {
    teacher: teacherWithInstrumentsAndAbsences;
    instruments: planningClientOptions["allInstruments"];
    formulas: planningClientOptions["allFormulas"];
  },
  initial: planningClientOptions["allInstruments"][number] | null,
): Promise<Field<{ id: instrumentid; description: string }>> {
  if (opts.teacher.instruments.length === 0) {
    return errorField(
      <a
        href={routes.teacher_editRoute.link({
          internalteacherid: opts.teacher.internalteacherid,
        })}
      >
        {"Koppel eerst een cursus aan deze docent."}
      </a>,
    );
  }
  const instrumentsFiltered = opts.instruments.filter(
    (inst) =>
      opts.teacher.instruments.some((teaches) => teaches.id === inst.id) &&
      opts.formulas.some((f) => f.instruments.includes(inst.id)),
  );
  return selectBox({
    values: instrumentsFiltered,
    initial:
      initial !== null && instrumentsFiltered.some((f) => f.id === initial.id)
        ? initial
        : instrumentsFiltered.length === 1
        ? instrumentsFiltered[0]
        : null,
    show: (inst) => inst?.description || "",
    label: "Cursus",
  });
}

export function makeFormulaValuesField<PRICE extends currency>(
  preview: {
    sources: { durationS: Source<number> };
  },
  deviateFromFormulaS: Source<boolean>,
  initial: {
    type_:
      | { tag: "Private" }
      | { tag: "Group"; maxnumberofstudents: number }
      | { tag: "Camp"; maxnumberofstudentscamp: number };
    numberoflessons: number;
    lessonduration: number;
    invoicelines: {
      description: string;
      amount_with_vat: PRICE;
      amount_without_vat: PRICE | null;
      vat: number;
      type_: invoicelinetype;
      chargemode: formulachargemode;
    }[];
  },
  formula: { type_: formulaType },
  country: country,
): Field<{
  numberoflessons: number;
  maxnumberofstudents: number;
  lessonduration: number;
  invoicelines: {
    description: string;
    amount_with_vat: PRICE;
    amount_without_vat: PRICE | null;
    vat: number;
    type_: invoicelinetype;
    chargemode: formulachargemode;
  }[];
  type_: formulaType;
}> {
  const showMaxNumberOfStudentsBox =
    initial.type_.tag === "Group" || formula.type_.tag === "Camp";
  return new Form({})
    .addField("numberoflessons", [], function () {
      return numberBox(initial.numberoflessons, {
        label: "Aantal lessen",
      });
    })
    .addField("maxnumberofstudents", [], function () {
      if (showMaxNumberOfStudentsBox) {
        return numberBox(
          initial.type_.tag === "Group"
            ? initial.type_.maxnumberofstudents
            : initial.type_.tag === "Camp"
            ? initial.type_.maxnumberofstudentscamp
            : 1,
          {
            label: "Aantal studenten",
          },
        );
      } else {
        return constantField(1);
      }
    })
    .addField(
      "type_",
      ["maxnumberofstudents"],
      async function ({ maxnumberofstudents }) {
        return maxnumberofstudents === 1
          ? constantField({ tag: "Private" as const })
          : formula.type_.tag == "Group"
          ? constantField({ tag: "Group" as const, maxnumberofstudents })
          : formula.type_.tag === "Camp"
          ? constantField({
              tag: "Camp" as const,
              maxnumberofstudentscamp: maxnumberofstudents,
            })
          : constantField({ tag: "Group" as const, maxnumberofstudents });
      },
    )
    .addField("lessonduration", [], function () {
      preview.sources.durationS.set(initial.lessonduration);
      return selectBox({
        values: lessonDurationOptions,
        initial: initial.lessonduration,
        show: (n) => {
          const renderMinutes = (n: number) =>
            "%n minuten".replace("%n", n.toString());
          return renderMinutes(n);
        },
        label: "Lesduur",
        previewS: preview.sources.durationS,
      });
    })
    .addField("invoicelines", [], function () {
      return Promise.resolve(
        formulaInvoicelinesForm<PRICE>(initial.invoicelines, {
          country,
          small: true,
          hideEditInPopup: true,
        }),
      );
    })
    .build({
      render: (fields) => (
        <div>
          {dynStyle(
            deviateFromFormulaS,
            (deviate) => (deviate ? "display: none;" : ""),
            <div style="text-align: right; font-size: 13px; margin-top: -16px; margin-bottom: 8px; ">
              <a onclick={() => deviateFromFormulaS.set(true)}>
                {"Wijk af van formule"}
              </a>
            </div>,
          )}
          {dynStyle(
            deviateFromFormulaS,
            (deviate) => (deviate ? "" : "display: none;"),
            <div>
              {showMaxNumberOfStudentsBox ? (
                <div>
                  <div className={style.equal_columns}>
                    {fields.numberoflessons()}
                    {fields.maxnumberofstudents()}
                  </div>
                  {fields.lessonduration()}
                </div>
              ) : (
                <div className={style.equal_columns}>
                  {fields.numberoflessons()}
                  {fields.lessonduration()}
                </div>
              )}
              {fields.invoicelines()}
            </div>,
          )}
        </div>
      ),
    });
}

export function errorField<T>(error: string | HTMLElement): Promise<Field<T>> {
  return Promise.resolve({
    s: new Source({ tag: "err", label: "" }),
    cleanup: () => {},
    render: () => <div style="color: red;">{error}</div>,
  });
}

export type editLessongroupFormOptions = {
  today: joda.LocalDate;
  teacher: teacherWithInstrumentsAndAbsences;
  slots: planning_slot[];
  instruments: planningClientOptions["allInstruments"];
  formulas: planningClientOptions["allFormulas"];
  period: period & { holidays: holiday[] };
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> };
  datefrom: joda.LocalDate;
  reload: () => void;
  handleDeleteDone: () => void;
  country: country;
  teacherMode: { teacherid: teacherid; onlyEditExistingLgs: boolean } | null;
};

export function renderEditLessonForm(
  l: planning_slot_contents_lesson,
  opts: editLessongroupFormOptions,
) {
  const form = new Form({})
    .addField("instrument", [], function () {
      return instrumentField(opts, l.instrument);
    })
    .addField("formula", ["instrument"], function ({ instrument }) {
      return formulaField(
        {
          ...opts,
          weekday: parseWeekday(l.lessongroup.weekday),
          limitBy: {
            tag: "slots",
            // Not sure if OK for biweekly?
            slots: opts.slots.filter(
              (sl) =>
                sl.contents.tag !== "lesson" ||
                sl.contents.lessongroup.id !== l.lessongroup.id,
            ),
          },
        },
        l.lesson.time,
        instrument,
        opts.formulas.find((f) => f.id === l.formula.id)!,
      );
    })
    .addField("formulaValues", ["formula"], async function ({ formula }) {
      const deviateFromFormulaS = new Source(false);
      const invoiceLines = await rpc(
        routes.getInvoiceLinesForLessongroupSpec,
        undefined,
        {
          lessongroupid: l.lessongroup.id,
          trial_lesson: false,
        },
      );
      const lessons = await rpc(
        routes.getActiveLessonsForLessongroupSpec,
        undefined,
        {
          lessongroupid: l.lessongroup.id,
        },
      );
      return makeFormulaValuesField<price_adapted_to_lg>(
        opts,
        deviateFromFormulaS,
        // If we didn't change the formula, we keep the current formula values too
        formula.id === l.formula.id
          ? {
              type_: parseFormulaType(l.lessongroup.type),
              lessonduration: l.lessongroup.lessonduration,
              invoicelines: invoiceLines,
              numberoflessons: lessons.length,
            }
          : {
              type_: formula.type_,
              lessonduration: formula.lessonduration,
              invoicelines: formula.invoicelines.map(
                (il): invoicelineLG => adaptInvoiceLineToLg(il),
              ),
              numberoflessons: formula.numberoflessons,
            },
        formula,
        opts.country,
      );
    })
    .addField(
      "location",
      ["instrument", "formulaValues"],
      async function ({ instrument, formulaValues }) {
        return locationField(
          l.lesson.date,
          l.lessongroup.time,
          formulaValues,
          opts,
          instrument,
          {
            roomId: l.lessongroup.roomId,
            location: l.lessongroup.location,
          },
          l.lessongroup.id,
        );
      },
    )
    .addField(
      "predictions",
      ["formula", "formulaValues", "location", "instrument"],
      async function ({ formula, formulaValues, location, instrument }) {
        const actions = await rpc(
          routes.getFormulaChangeActionsSpec,
          undefined,
          {
            lessongroupId: l.lessongroup.id,
            datefrom: opts.datefrom,
            formula: {
              ...formula,
              ...formulaValues,
            },
            location: {
              location: location.location,
              roomId: location.roomid,
            },
          },
        );

        const renderedFormulaChangeActions = (
          <section>
            <ul style="font-size: 0.85em; font-weight: 600; list-style: disc; padding-left: 16px;">
              {instrument.id === l.instrument.id ? (
                <span></span>
              ) : (
                <li>{"Het instrument zal aangepast worden"}</li>
              )}
              {actions.changeLocation === null ? (
                <span></span>
              ) : (
                <li>
                  {l.lessongroup.firstLesson &&
                  l.lessongroup.firstLesson.isEqual(opts.datefrom)
                    ? "Het lokaal zal aangepast worden"
                    : "Het lokaal zal aangepast worden vanaf %d".replace("%d", opts.datefrom.format(ddMMyyyy))}
                </li>
              )}
              {actions.changeType === null ? (
                <span></span>
              ) : (
                <li>
                  {"Het type les zal veranderen naar %formulatype".replace(
                    "%formulatype",
                    renderFormulaType(actions.changeType),
                  )}
                </li>
              )}
              {actions.changePlanningMode === null ? (
                <span></span>
              ) : (
                <li>
                  {"Het planningstype zal veranderen naar %planningmode. Aan de lesdata zelf zal niets automatisch veranderen".replace(
                    "%planningmode",
                    renderPlanningMode(actions.changePlanningMode),
                  )}
                </li>
              )}
              {actions.changeLessonDuration === null ? (
                <span></span>
              ) : (
                <li>
                  {"De lesduur zal veranderen naar %n minuten".replace(
                    "%n",
                    actions.changeLessonDuration.toString(),
                  )}
                </li>
              )}
              {actions.changeInvoiceLines === null ? (
                <span></span>
              ) : (
                <li>
                  {"De factuurlijnen zullen veranderen, maar betalingen en creditnota's blijven behouden"}
                </li>
              )}
              {actions.removeLessons.length === 0 ? (
                <span></span>
              ) : (
                <li>
                  {"Er zullen %n lessen verwijderd worden".replace(
                    "%n",
                    actions.removeLessons.length.toString(),
                  )}
                </li>
              )}
              {actions.addLessons.length === 0 ? (
                <span></span>
              ) : (
                <li>
                  {"Er zullen %n lessen bijgemaakt worden".replace(
                    "%n",
                    actions.addLessons.length.toString(),
                  )}
                </li>
              )}
            </ul>
          </section>
        );

        return constantFieldWithRender(0, renderedFormulaChangeActions);
      },
    )
    .build();

  return form;
}

export function makeMoveForm(
  l: {
    lessongroupid: lessongroupid;
    lessonduration: number;
    instrument: { id: instrumentid };
    room: {
      description: string | null;
      location: lesson_location;
      roomId: roomid | null;
    };
  },
  movingTo: {
    initialTime: joda.LocalTime;
    firstDestinationDate: joda.LocalDate;
    teacher: {
      internalteacherid: internalteacherid;
    };
  },
  sources: { timeS: Source<joda.LocalTime> },
) {
  return new Form({})
    .addField("time", [], () =>
      timeBox({
        initialVal: movingTo.initialTime,
        label: "Start",
        previewS: sources.timeS,
      }),
    )
    .addField("location", ["time"], async ({ time }) => {
      return await locationField(
        movingTo.firstDestinationDate,
        time,
        { lessonduration: l.lessonduration },
        { teacher: { internalteacherid: movingTo.teacher.internalteacherid } },
        l.instrument,
        l.room,
        l.lessongroupid,
      );
    })
    .build();
}

export function pickDatesOnYearCalendarField(opts: {
  initial: joda.LocalDate[];
  skip: joda.LocalDate[];
  max: number;
  period: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
    holidays: holiday[];
  };
  nextPeriod: null | (period & { holidays: holiday[] });
  limitWeekday: joda.DayOfWeek | null;
  showFlexExplanation: boolean;
}): Promise<Field<joda.LocalDate[]>> {
  const s: Source<Parsing<joda.LocalDate[]>> = new Source({
    tag: "parsed",
    parsed: opts.initial,
  });

  const isOpenS = new Source(false);
  const showAllLessonsInCurrentYearS = new Source(false);
  const showNextPeriodS = new Source(false);

  function render() {
    return dynVariadic(
      [isOpenS, s, showAllLessonsInCurrentYearS, showNextPeriodS],
      ([isOpen, curr, showAllLessonsInCurrentYear, showNextPeriod]) => {
        if (curr.tag !== "parsed") {
          return <span></span>;
        }
        const summaryInText = "%n lesdagen geselecteerd".replace(
          "%n",
          curr.parsed.length.toString() + "/" + opts.max.toString(),
        );
        if (isOpen === false) {
          return [
            <label>
              <div>{"Lesdagen"}</div>
              <div>
                <a
                  className={style.input_like}
                  onclick={() => isOpenS.set(true)}
                >
                  {summaryInText}
                  <div style="font-size: 0.7em">▼</div>
                </a>
              </div>
            </label>,
            opts.showFlexExplanation === true ? (
              <div className={style.formsection_informational}>
                {"Deze formule is een beurtenkaart. Docent en admin kunnen de volgende lessen door het jaar heen plannen."}
              </div>
            ) : (
              <span></span>
            ),
          ];
        } else {
          const smallTextRowStyle =
            " font-size: 13px; text-align: right; margin-top: -16px;";
          const showAllLessonsToggle =
            showAllLessonsInCurrentYear === true ? (
              <span></span>
            ) : (
              <div style={smallTextRowStyle}>
                <a
                  onclick={() => showAllLessonsInCurrentYearS.set(true)}
                  style="float: right"
                >
                  {"Toon alle dagen van het schooljaar"}
                </a>
              </div>
            );

          function addDate(d: joda.LocalDate) {
            if (curr.tag === "parsed") {
              s.set({
                tag: "parsed",
                parsed: curr.parsed.concat(d).orderBy((d) => d.toEpochDay()),
              });
            }
          }
          function removeDate(d: joda.LocalDate) {
            if (curr.tag === "parsed") {
              s.set({
                tag: "parsed",
                parsed: curr.parsed.filter((pd) => !pd.isEqual(d)),
              });
            }
          }

          const tablCurrentPeriod = makeLessonTableForPeriod(
            opts.period,
            curr,
            removeDate,
            addDate,
            showAllLessonsInCurrentYear,
          );

          const tablNextPeriod =
            opts.nextPeriod === null ? (
              <span></span>
            ) : (
              makeLessonTableForPeriod(
                opts.nextPeriod,
                curr,
                removeDate,
                addDate,
                showAllLessonsInCurrentYear,
              )
            );

          const showNextPeriodToggle =
            showNextPeriod === true ? (
              <span></span>
            ) : (
              <article
                style={
                  smallTextRowStyle + "text-align: right; padding-top: 8px"
                }
              >
                <a onclick={() => showNextPeriodS.set(true)}>
                  {"Toon volgend schooljaar"}
                </a>
              </article>
            );

          return (
            <div>
              <label>{"Lesdagen"}</label>
              {showAllLessonsToggle}

              {tablCurrentPeriod}

              {tablNextPeriod}

              {showNextPeriodToggle}

              <article style="display: flex; justify-content: flex-end;">
                <span
                  className={style.input_like}
                  style="flex: 1; margin: 0px; width: auto;"
                >
                  <i className={FA.icon_info_solid}></i>
                  {summaryInText}
                </span>
                <button
                  style="margin-left: 8px"
                  onclick={() => isOpenS.set(false)}
                >
                  <i className={FA.icon_compress}></i>
                  {"Verklein"}
                </button>
              </article>
            </div>
          );
        }
      },
    );
  }

  return Promise.resolve({ s, render, cleanup: () => {} });

  function makeLessonTableForPeriod(
    period: {
      firstday: joda.LocalDate;
      lastday: joda.LocalDate;
      holidays: holiday[];
    },
    curr: { tag: "parsed"; parsed: joda.LocalDate[] },
    removeDate: (d: joda.LocalDate) => void,
    addDate: (d: joda.LocalDate) => void,
    showAllLessonsInCurrentYear: boolean,
  ) {
    const mondayOfFirstWeek =
      showAllLessonsInCurrentYear ||
      period.firstday.isAfter(joda.LocalDate.now())
        ? getMonday(period.firstday)
        : getMonday(joda.LocalDate.now());
    const sundayOfLastWeek = getFirstWeekdayFrom(
      period.lastday,
      joda.DayOfWeek.SUNDAY,
    );

    return (
      <table className={style.table_condensed}>
        <tbody>
          {chunk(makeDateRange(mondayOfFirstWeek, sundayOfLastWeek), 7).map(
            (weekdays, i) => {
              const isFirstOfMonth =
                i === 0 || weekdays.some((d) => d.dayOfMonth() === 1);
              return (
                <tr>
                  <th style="font-weight: normal; width: 65px">
                    <label>
                      {isFirstOfMonth
                        ? translateMonth(weekdays.last()!.month()).slice(0, 3)
                        : ""}
                    </label>
                  </th>
                  {weekdays.map((d) => {
                    const outsidePeriod =
                      d.isBefore(period.firstday) || d.isAfter(period.lastday);
                    const notCorrectWeekday =
                      opts.limitWeekday === null
                        ? false
                        : !isEqual(d.dayOfWeek(), opts.limitWeekday);
                    const isHoliday = period.holidays.some((h) =>
                      dateInDaterange(
                        { date: d },
                        { from: h.firstday, until: h.lastday },
                      ),
                    );
                    const isSkipped = opts.skip.some((skipD) =>
                      skipD.isEqual(d),
                    );
                    const isSelected = curr.parsed.some((selectedD) =>
                      selectedD.isEqual(d),
                    );

                    const onclick =
                      outsidePeriod ||
                      notCorrectWeekday ||
                      isHoliday ||
                      isSkipped
                        ? undefined
                        : isSelected
                        ? () => removeDate(d)
                        : () => addDate(d);

                    const sty = outsidePeriod
                      ? " background-color: white; visibility: hidden; border: none; "
                      : isHoliday
                      ? "background-color: grey"
                      : notCorrectWeekday
                      ? "background-color: darkgrey"
                      : isSkipped
                      ? "background-color: #ffe7f2;"
                      : isSelected
                      ? "background-color: #4D8EF3; color: white; cursor: pointer; "
                      : // open
                        "background-color: white; cursor: pointer;";

                    return (
                      <td style={sty} onclick={onclick}>
                        {d.dayOfMonth().toString()}
                      </td>
                    );
                  })}
                </tr>
              );
            },
          )}
        </tbody>
      </table>
    );
  }
}

export function renderPlanningRemarks(
  s: string[],
  opts?: { bold?: boolean; red?: boolean },
) {
  const st =
    (opts?.bold === true ? "font-weight: bold;" : "") +
    (opts?.red === true ? "color: red;" : "");
  return s.length === 0 ? (
    <span></span>
  ) : s.length === 1 ? (
    <article className={style.formsection_informational} style={st}>
      {s[0]}
    </article>
  ) : (
    <article className={style.formsection_informational} style={st}>
      <ul style="list-style: disc; padding-left: 16px;">
        {s.map((s) => (
          <li>{s}</li>
        ))}
      </ul>
    </article>
  );
}
